/**
 * Realtime — DESIGN.md §15.
 *
 * "Message = envelope only … The client invalidates TanStack Query keys and
 * refetches — no state merging over the wire, so SSE bugs can't corrupt data,
 * and offline/reconnect (built into `EventSource`) self-heals with
 * at-most-a-refetch."
 *
 * This file is the whole client half of that: one `EventSource` per tab, an
 * envelope → query-key mapping, and nothing else. It deliberately never writes
 * data into the cache.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE } from './api';
import { queryKeys } from './keys';
import { useAuth } from './auth';

interface Envelope {
  type: string;
  taskId?: string;
  projectIds?: string[];
  userId?: string;
  entityId?: string;
}

interface RealtimeValue {
  /** True while the stream is open. Surfaced so the UI can be honest (§15). */
  connected: boolean;
  /** Declare interest in extra entity channels for this tab. */
  subscribe: (channels: { projectIds?: string[]; taskIds?: string[] }) => void;
}

const RealtimeContext = createContext<RealtimeValue | null>(null);

export function RealtimeProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const [connected, setConnected] = useState(false);
  const [channels, setChannels] = useState<{ projectIds: string[]; taskIds: string[] }>({
    projectIds: [],
    taskIds: [],
  });

  const subscribe = useCallback((next: { projectIds?: string[]; taskIds?: string[] }) => {
    setChannels((current) => {
      const projectIds = next.projectIds ?? current.projectIds;
      const taskIds = next.taskIds ?? current.taskIds;
      // Reference equality matters: this value is an effect dependency, and a
      // fresh object on every render would reconnect the stream continuously.
      if (same(projectIds, current.projectIds) && same(taskIds, current.taskIds)) return current;
      return { projectIds, taskIds };
    });
  }, []);

  const handlerRef = useRef<(envelope: Envelope) => void>(() => {});
  handlerRef.current = (envelope: Envelope) => {
    switch (envelope.type) {
      case 'task.created':
      case 'task.deleted':
        void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
        break;
      case 'task.changed':
        void queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
        break;
      case 'comment.changed':
        if (envelope.taskId) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.taskComments(envelope.taskId),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.taskActivity(envelope.taskId),
          });
        }
        break;
      case 'project.changed':
        void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
        break;
      case 'lookup.changed':
        void queryClient.invalidateQueries({ queryKey: queryKeys.lookups });
        break;
      case 'user.changed':
        void queryClient.invalidateQueries({ queryKey: queryKeys.users });
        break;
      case 'notification.created':
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
        void queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount });
        break;
      default:
        break;
    }
    // Every envelope can produce a notification for somebody, so the badge is
    // refreshed regardless of type. It is one cheap request.
    if (envelope.type !== 'notification.created') {
      void queryClient.invalidateQueries({ queryKey: queryKeys.unreadCount });
    }
  };

  useEffect(() => {
    if (!me) {
      setConnected(false);
      return;
    }

    const url = new URL(`${API_BASE}/events/stream`, window.location.origin);
    if (channels.projectIds.length > 0) {
      url.searchParams.set('projectIds', channels.projectIds.join(','));
    }
    if (channels.taskIds.length > 0) url.searchParams.set('taskIds', channels.taskIds.join(','));

    const source = new EventSource(url, { withCredentials: true });

    source.onopen = () => setConnected(true);
    source.onerror = () => {
      // `EventSource` reconnects on its own; this only reflects the state.
      // §15's degradation clause is what makes that acceptable — polling and
      // refetch-on-focus keep the app correct while the stream is down.
      setConnected(false);
    };

    const onMessage = (event: MessageEvent<string>): void => {
      try {
        handlerRef.current(JSON.parse(event.data) as Envelope);
      } catch {
        // A malformed envelope is a server bug; the next refetch heals it.
      }
    };

    // The server names each event by its envelope type, so every type has to
    // be registered — `onmessage` alone would only catch unnamed events.
    for (const type of ENVELOPE_TYPES) source.addEventListener(type, onMessage);

    return () => {
      for (const type of ENVELOPE_TYPES) source.removeEventListener(type, onMessage);
      source.close();
      setConnected(false);
    };
  }, [me, channels]);

  return (
    <RealtimeContext.Provider value={{ connected, subscribe }}>{children}</RealtimeContext.Provider>
  );
}

const ENVELOPE_TYPES = [
  'task.changed',
  'task.created',
  'task.deleted',
  'comment.changed',
  'project.changed',
  'notification.created',
  'lookup.changed',
  'view.changed',
  'user.changed',
  'timer.changed',
] as const;

export function useRealtime(): RealtimeValue {
  const value = useContext(RealtimeContext);
  // Rendering outside the provider (a test, a storybook) should degrade, not
  // crash: the app is required to work without SSE at all (§15).
  return value ?? { connected: false, subscribe: () => {} };
}

/** Declare the entity channels a screen cares about while it is mounted. */
export function useSubscribe(channels: { projectIds?: string[]; taskIds?: string[] }): void {
  const { subscribe } = useRealtime();
  const projectKey = (channels.projectIds ?? []).join(',');
  const taskKey = (channels.taskIds ?? []).join(',');
  useEffect(() => {
    subscribe({
      projectIds: projectKey ? projectKey.split(',') : [],
      taskIds: taskKey ? taskKey.split(',') : [],
    });
  }, [subscribe, projectKey, taskKey]);
}

function same(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}
